// ============================================================================
// LEGAL GATEWAY - DOCUMENT SIGNATURE FLOW
// Life & Wealth Solutions V10.X
// ============================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const LegalGateway = () => {
    const { linkToken } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [legalStatus, setLegalStatus] = useState(null);
    const [linkInfo, setLinkInfo] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState('checking'); // checking, pending, signing, complete, error

    useEffect(() => {
        checkLegalStatus();
    }, [linkToken]);

    const checkLegalStatus = async () => {
        try {
            setLoading(true);

            // Verify the link first
            const linkRes = await axios.post(`/api/demo/access/${linkToken}`);

            if (!linkRes.data.success && linkRes.data.requiresLegalGateway) {
                setLinkInfo(linkRes.data);
                loadLegalDocumentsStatus(linkRes.data.legalDocumentsId);
            } else if (linkRes.data.success) {
                // Legal gateway already passed, redirect to demo
                window.location.href = linkRes.data.demoAccess.demoUrl;
            }
        } catch (error) {
            console.error('Legal check error:', error);
            setError(error.response?.data?.error || 'Failed to verify link');
            setStep('error');
            setLoading(false);
        }
    };

    const loadLegalDocumentsStatus = async (legalDocsId) => {
        try {
            const response = await axios.get(`/api/demo/legal/status/${legalDocsId}`);

            setLegalStatus(response.data.legalDocuments);

            if (response.data.legalDocuments.allComplete) {
                setStep('complete');
                setTimeout(() => {
                    // Redirect to demo after 3 seconds
                    checkLegalStatus();
                }, 3000);
            } else if (response.data.legalDocuments.verificationStatus === 'pending') {
                setStep('pending');
            } else {
                setStep('signing');
            }

            setLoading(false);
        } catch (error) {
            console.error('Legal status error:', error);
            setError('Failed to load legal documents status');
            setStep('error');
            setLoading(false);
        }
    };

    const handleInitiateSigning = async () => {
        try {
            setLoading(true);

            // Get link details for recipient info
            const response = await axios.post('/api/demo/legal/initiate', {
                specialLinkId: linkInfo.legalDocumentsId,
                recipientEmail: linkInfo.recipientEmail,
                recipientName: linkInfo.recipientName,
                recipientCompany: linkInfo.recipientCompany,
                stateCode: linkInfo.stateCode
            });

            if (response.data.success) {
                alert('✅ Legal documents sent to your email!\n\nPlease check your inbox and complete the signatures.');
                setStep('pending');
                
                // Poll for completion every 30 seconds
                startPolling();
            }

            setLoading(false);
        } catch (error) {
            console.error('Signing initiation error:', error);
            setError('Failed to send legal documents');
            setLoading(false);
        }
    };

    const startPolling = () => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await axios.get(`/api/demo/legal/status/${linkInfo.legalDocumentsId}`);

                if (response.data.legalDocuments.allComplete) {
                    clearInterval(pollInterval);
                    setStep('complete');
                    setTimeout(() => {
                        checkLegalStatus();
                    }, 3000);
                }
            } catch (error) {
                console.error('Polling error:', error);
            }
        }, 30000); // Poll every 30 seconds

        // Stop polling after 1 hour
        setTimeout(() => clearInterval(pollInterval), 3600000);
    };

    if (loading && step === 'checking') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Verifying your access...</p>
                </div>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Error</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'complete') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Documents Complete!</h2>
                        <p className="text-gray-600 mb-6">
                            All legal documents have been signed and verified.
                            Redirecting to your demo...
                        </p>
                        <div className="animate-pulse text-purple-600 font-semibold">
                            Loading demo environment...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="bg-white rounded-full h-20 w-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <svg className="h-10 w-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Legal Gateway</h1>
                    <p className="text-gray-600 text-lg">
                        Before accessing your exclusive demo, please complete the required legal documents
                    </p>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-lg shadow-xl p-8 mb-6">
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Required Documents</h2>
                        <p className="text-gray-600 mb-6">
                            To protect your information and our intellectual property, we require the following documents to be signed:
                        </p>

                        <div className="space-y-4">
                            {/* NDA */}
                            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                                <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${
                                    legalStatus?.documents.nda.status === 'signed' ? 'bg-green-500' : 'bg-gray-300'
                                }`}>
                                    {legalStatus?.documents.nda.status === 'signed' && (
                                        <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">Non-Disclosure Agreement (NDA)</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Protects confidential information and trade secrets
                                    </p>
                                    {legalStatus?.documents.nda.status === 'signed' && (
                                        <p className="text-xs text-green-600 mt-1">
                                            ✓ Signed on {new Date(legalStatus.documents.nda.signedAt).toLocaleString()}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Demo Agreement */}
                            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                                <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${
                                    legalStatus?.documents.demoAgreement.status === 'signed' ? 'bg-green-500' : 'bg-gray-300'
                                }`}>
                                    {legalStatus?.documents.demoAgreement.status === 'signed' && (
                                        <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">Demo Access Agreement</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Defines terms of use for the demo environment
                                    </p>
                                    {legalStatus?.documents.demoAgreement.status === 'signed' && (
                                        <p className="text-xs text-green-600 mt-1">
                                            ✓ Signed on {new Date(legalStatus.documents.demoAgreement.signedAt).toLocaleString()}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* IP Agreement */}
                            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                                <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center ${
                                    legalStatus?.documents.ipAgreement.status === 'signed' ? 'bg-green-500' : 'bg-gray-300'
                                }`}>
                                    {legalStatus?.documents.ipAgreement.status === 'signed' && (
                                        <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">Intellectual Property Agreement</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Protects our proprietary technology and AI systems
                                    </p>
                                    {legalStatus?.documents.ipAgreement.status === 'signed' && (
                                        <p className="text-xs text-green-600 mt-1">
                                            ✓ Signed on {new Date(legalStatus.documents.ipAgreement.signedAt).toLocaleString()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="text-center pt-6 border-t border-gray-200">
                        {step === 'signing' && (
                            <button
                                onClick={handleInitiateSigning}
                                disabled={loading}
                                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-colors text-lg"
                            >
                                {loading ? 'Sending...' : 'Send Documents for Signature'}
                            </button>
                        )}

                        {step === 'pending' && (
                            <div>
                                <div className="animate-pulse text-purple-600 font-semibold mb-4">
                                    Waiting for signatures...
                                </div>
                                <p className="text-sm text-gray-600 mb-4">
                                    Please check your email and complete all document signatures.
                                    This page will automatically refresh when complete.
                                </p>
                                <button
                                    onClick={() => loadLegalDocumentsStatus(linkInfo.legalDocumentsId)}
                                    className="text-purple-600 hover:text-purple-800 font-medium"
                                >
                                    Refresh Status
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Info */}
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <p className="text-sm text-gray-600">
                        <strong>Privacy Guaranteed:</strong> All documents are encrypted and securely stored.
                        Your information will never be shared with third parties.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                        Powered by DocuSeal • ESIGN & UETA Compliant Electronic Signatures
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LegalGateway;
